/**
 * Locale formatting — G6.
 *
 * G6 is blunt about why this is not a detail: "Decimal comma vs point (IT and ES use
 * comma — a planning system that shows `1,234` meaning one-point-two-three-four to an
 * Italian planner has lost their trust permanently)."
 *
 * The rules encoded here:
 *   · numbers in the grid are locale-formatted for DISPLAY and raw in EXPORT, and
 *     the two must never be confused (G6, G2.4) — nothing in src/export/ imports
 *     this module, and a test asserts that
 *   · currency: symbol before value, thousands separators per locale, NO DECIMALS
 *     ABOVE 1,000, negatives with a minus sign — never parentheses (ADR 9061) — AND the
 *     negative colour token, never colour alone (design/09 §4.3, §5.2 rule 4)
 *   · ISO-8601 week numbering, Monday start, is the platform standard for retail
 *     weeks (G6)
 *   · all timestamps stored UTC, displayed in the user's timezone (G6)
 */

export const SUPPORTED_LOCALES = ['en-GB', 'it-IT', 'es-419'] as const;
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];
export const SOURCE_LOCALE: SupportedLocale = 'en-GB';

/** `Intl` does not know `es-419` as a formatting locale in every runtime; the
 *  fallback chain G6 specifies is locale → language → en-GB. */
const INTL_FALLBACK: Record<string, string> = { 'es-419': 'es-MX' };
const intlLocale = (locale: string): string => INTL_FALLBACK[locale] ?? locale;

const cache = new Map<string, Intl.NumberFormat>();
/**
 * The one door every DISPLAYED figure goes through. The thousands separator is ALWAYS on:
 * CLDR groups it-IT and es-419 only from five digits (`minimumGroupingDigits: 2`), so `Intl`
 * alone prints «7661» beside «62.802» on the same page (production audit 2026-10-11; owner
 * decision: group from four digits, everywhere). A caller that must NOT group — an editable
 * input, a stepper, an identifier — passes `useGrouping: false` explicitly and wins.
 */
function numberFormat(locale: string, options: Intl.NumberFormatOptions): Intl.NumberFormat {
  const key = `${locale}|${JSON.stringify(options)}`;
  let fmt = cache.get(key);
  if (!fmt) {
    fmt = new Intl.NumberFormat(intlLocale(locale), { useGrouping: 'always', ...options });
    cache.set(key, fmt);
  }
  return fmt;
}

/**
 * A cached `Intl.NumberFormat` for DISPLAY with grouping always on — for the screens that need
 * a formatter object (several figures from one instance, `formatToParts`) rather than one of the
 * `format*` helpers below. Never use it for an export (raw numbers, G6/G2.4) or for a value the
 * planner edits; those pass `useGrouping: false` or do not format at all.
 */
export function displayNumberFormat(locale: string, options: Intl.NumberFormatOptions = {}): Intl.NumberFormat {
  return numberFormat(locale, options);
}

/** design/09 §4.3 — no decimals above 1,000. */
function fractionDigitsFor(value: number, explicit?: number): number {
  if (explicit !== undefined) return explicit;
  return Math.abs(value) >= 1000 ? 0 : 2;
}

export interface NumberFormatOptions {
  readonly fractionDigits?: number;
  /**
   * Always show `fractionDigits` decimals, trailing zeros included. Off by default: a lone
   * figure reads better as `0.4`. On for a GROUP of figures a reader compares — `0.32`,
   * `0.40`, `0.09` line up; `0.32`, `0.4`, `0.09` look like three precisions (audit v4 G-35).
   */
  readonly fixed?: boolean;
}

/**
 * A negative number carries a minus sign — Intl's own, the glyph every other number in the app
 * shows — and NEVER accounting parentheses (ADR 9061; owner decision 2026-10-13: a client read
 * «(1)» as something other than «-1»). `signDisplay: 'negative'` is Intl's default minus the
 * sign on a negative ZERO, so a value that rounds to nothing reads plain «0», not «-0».
 * §5.2 rule 4 (a negative is never carried by colour alone) is met by that sign.
 */
const SIGN_NEGATIVE_ONLY = { signDisplay: 'negative' } as const satisfies Intl.NumberFormatOptions;

export function formatNumber(value: number, locale: string, options: NumberFormatOptions = {}): string {
  const digits = fractionDigitsFor(value, options.fractionDigits);
  return numberFormat(locale, {
    minimumFractionDigits: options.fixed === true ? digits : 0, maximumFractionDigits: digits, ...SIGN_NEGATIVE_ONLY,
  }).format(value);
}

export function formatInteger(value: number, locale: string): string {
  return numberFormat(locale, { maximumFractionDigits: 0 }).format(value);
}

/**
 * A whole count with its thousands separator ALWAYS. Since the owner's 2026-10-11 decision every
 * helper here groups from four digits, so this is `formatInteger` under the name call sites used
 * when only the Stock headline did — kept so those sites read as before.
 */
export function formatGroupedInteger(value: number, locale: string): string {
  return formatInteger(value, locale);
}

export function formatPercent(value: number, locale: string, fractionDigits = 1): string {
  return numberFormat(locale, {
    style: 'percent', minimumFractionDigits: 0, maximumFractionDigits: fractionDigits,
  }).format(value);
}

/**
 * design/09 §4.3 — "symbol before value, thousands separators per locale, no decimals
 * above 1,000, negative values AND the negative colour token (never colour alone)".
 *
 * The negative is carried by its minus sign, produced here by Intl — «-£1,234», never
 * «(£1,234)» (ADR 9061, which supersedes the parentheses §4.3 asked for). The colour token is
 * applied by <DeltaValue>. Two carriers, as §5.2 rule 4 requires.
 */
export function formatCurrency(value: number, currency: string, locale: string): string {
  const digits = fractionDigitsFor(value);
  return numberFormat(locale, {
    style: 'currency', currency,
    currencyDisplay: 'symbol',
    minimumFractionDigits: 0,
    maximumFractionDigits: digits,
    ...SIGN_NEGATIVE_ONLY,
  }).format(value);
}

/** Axis ticks and grid cells: three significant digits maximum, per G2 §2.5. */
export function formatCompact(value: number, locale: string): string {
  return numberFormat(locale, { maximumFractionDigits: Math.abs(value) < 10 ? 1 : 0 }).format(value);
}

// ── the compact figure — audit v6 rule 4, ADR 9013 ───────────────────────────

/**
 * Above this magnitude a figure on a KPI, a bar cell or a headline is shown compact —
 * `2.41M` rather than `2,408,836` (audit v6 §3, rule 4: "formato compatto sopra i 10.000").
 * At or below it the exact figure is short enough to read at a glance and is shown whole.
 */
export const COMPACT_THRESHOLD = 10_000;

export interface CompactNumber {
  /** What the cell shows. */
  readonly text: string;
  /** The exact figure, locale-grouped — for `title`, so the pointer reads the real number. */
  readonly exact: string;
  /** Whether `text` is an abbreviation of `exact` (and so needs `exact` beside it). */
  readonly compacted: boolean;
}

export interface CompactNumberOptions {
  /** Print `+` on a positive figure, as a variance does. A negative always carries its sign. */
  readonly signed?: boolean;
  /** Override `COMPACT_THRESHOLD` — for a surface whose own rule is stated elsewhere. */
  readonly threshold?: number;
}

/**
 * A figure for DISPLAY, compact above `COMPACT_THRESHOLD`, with the exact figure beside it.
 *
 * `Intl.NumberFormat`'s own compact notation, three significant digits, in the planner's
 * locale — `2.41M` (en-GB), `2,41 Mln` (it-IT), `2.41 M` (es-419). Never used for an export:
 * a file carries the raw number (G6, G2.4), and nothing in `src/export/` imports this module.
 *
 * **Italian has no short form for thousands**, and that is not cosmetic. CLDR's it-IT
 * compact-short pattern for 10³–10⁵ is the plain number, so `Intl` turns `136662` into
 * `"137.000"` — which an Italian planner reads as the EXACT figure 137,000. A rounded number
 * that looks exact is a wrong number. So when `Intl` returns no compact suffix
 * (`formatToParts` has no `compact` part) the exact figure is shown instead: it is at most
 * seven characters in that range, and it is true.
 */
export function formatCompactNumber(
  value: number,
  locale: string,
  options: CompactNumberOptions = {},
): CompactNumber {
  const signDisplay = options.signed === true ? 'exceptZero' : 'auto';
  const exact = numberFormat(locale, {
    maximumFractionDigits: Math.abs(value) >= 1000 ? 0 : 2,
    signDisplay,
  }).format(value);
  if (Math.abs(value) <= (options.threshold ?? COMPACT_THRESHOLD)) {
    return { text: exact, exact, compacted: false };
  }
  const parts = numberFormat(locale, {
    notation: 'compact',
    compactDisplay: 'short',
    maximumSignificantDigits: 3,
    signDisplay,
  }).formatToParts(value);
  if (!parts.some((part) => part.type === 'compact')) {
    return { text: exact, exact, compacted: false };
  }
  return { text: parts.map((part) => part.value).join(''), exact, compacted: true };
}

// ── dates and retail weeks ───────────────────────────────────────────────────

export function formatDate(iso: string, locale: string, timeZone: string): string {
  return new Intl.DateTimeFormat(intlLocale(locale), { dateStyle: 'medium', timeZone }).format(new Date(iso));
}

export function formatDateTime(iso: string, locale: string, timeZone: string): string {
  return new Intl.DateTimeFormat(intlLocale(locale), { dateStyle: 'medium', timeStyle: 'short', timeZone })
    .format(new Date(iso));
}

/**
 * ISO-8601 week number, Monday start. G6: "ISO-8601 week numbering (Monday start) is
 * the platform standard for retail weeks", with the retail calendar (4-5-4 or
 * equivalent) as configuration per country — which is why this function is the
 * platform default and not the only one a screen may use.
 */
export function isoWeek(date: Date): { year: number; week: number } {
  const d = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  // Thursday of the current ISO week determines the ISO year.
  const day = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - day);
  const year = d.getUTCFullYear();
  const firstThursday = new Date(Date.UTC(year, 0, 4));
  const firstDay = firstThursday.getUTCDay() || 7;
  firstThursday.setUTCDate(firstThursday.getUTCDate() + 4 - firstDay);
  const week = 1 + Math.round((d.getTime() - firstThursday.getTime()) / (7 * 86_400_000));
  return { year, week };
}

/** `2026-W31`. Stable across locales because it is an identifier, not prose. */
export function isoWeekLabel(date: Date): string {
  const { year, week } = isoWeek(date);
  return `${year}-W${String(week).padStart(2, '0')}`;
}

/** G6 — first day of week is locale configuration, not a constant. */
export function firstDayOfWeek(locale: string): 0 | 1 {
  // en-GB, it-IT and es-419 retail weeks all start Monday, and ISO-8601 is the
  // platform standard. The function exists so a fourth locale that starts Sunday is
  // a data change rather than a code change (G6 test f).
  return locale === 'en-US' ? 0 : 1;
}
